import {AVATAR_FILES_API_PATH, Person, PersonApi} from "@betrue/shared/interfaces";
import {gql, makeVar, useLazyQuery, useMutation, useQuery} from "@apollo/client";
import {useEffect, useState} from 'react';
import {BASE_ENTITY_FIELDS} from "./base-fields";
import {CONFIG} from "@betrue/ui";
import {ReactiveVar} from "@apollo/client/cache/inmemory/reactiveVars";
import {useReactiveResult, useResult} from "./results";

export const CORE_PERSON_FIELDS = gql`
  fragment CorePersonFields on PersonModel{
    ${BASE_ENTITY_FIELDS}
    firstName
    lastName
    email
    stripeAccountId
    externalId
    role
    scope
    hasAvatar
    personType
    jobTitle
    phone
    chatId
    profile {
      id
    }
  }
`

export const GET_CURRENT_PERSON = gql`
  ${CORE_PERSON_FIELDS}
  query GetCurrentPerson {
    current {
      ...CorePersonFields
    }
  }
`;

const UPDATE_PERSON = gql`
  ${CORE_PERSON_FIELDS}
  mutation UpdatePerson($person: PersonModelInput!) {
    updatePerson(person: $person) {
      ...CorePersonFields
    }
  }
`;

const GET_PERSON_BY_ID = gql`
  ${CORE_PERSON_FIELDS}
  query GetPersonById($id: String!) {
    getPersonById(id: $id) {
      ...CorePersonFields
    }
  }
`;

const UPDATE_AVATAR = gql`
  ${CORE_PERSON_FIELDS}
  mutation UpdateAvatar($id: String!,$avatar: String!) {
    updateAvatar(avatar: $avatar, id: $id) {
      ...CorePersonFields
    }
  }
`;


const CREATE_OFFLINE_USER = gql`
  ${CORE_PERSON_FIELDS}
  mutation CreateOfflineUser($firstName: String!,$lastName: String!,$email: String!) {
    createOfflinePerson(email: $email, firstName: $firstName, lastName: $lastName){
      ...CorePersonFields
    }
  }
`;

export const currentPerson = makeVar(null)

export const personAvatarUrl = (p: Person) => {
  return `${CONFIG.backend.url}/${AVATAR_FILES_API_PATH}/${p.id}?v=${p.version}`
}

export const usePerson = <T = Person | Person[]>(store?: ReactiveVar<T>): [T, PersonApi] => {
  const [result, setResult] = store ? useReactiveResult<T>(store) : useState<T>()
  const [getPersonById, personByIdResult] = useLazyQuery(GET_PERSON_BY_ID, {fetchPolicy: 'cache-and-network'})
  const [current, {loading, error, data}] = useLazyQuery(GET_CURRENT_PERSON, {fetchPolicy: 'cache-and-network'})
  const [updatePerson, updatePersonResult] = useMutation(UPDATE_PERSON)
  const [updateAvatar, updateAvatarResult] = useMutation(UPDATE_AVATAR)
  const createOfflinePerson = useResult(useMutation(CREATE_OFFLINE_USER), setResult)


  const fetch = (loading, error, data) => {
    if (!loading && data) {
      setResult(data)
    }
    if (error) {
      console.error(error)
    }
  }

  useEffect(() => {
    fetch(updateAvatarResult.loading, updateAvatarResult.error, updateAvatarResult.data?.updateAvatar)
  }, [updateAvatarResult])

  useEffect(() => {
    fetch(personByIdResult.loading, personByIdResult.error, personByIdResult.data?.getPersonById)
  }, [personByIdResult])

  useEffect(() => {
    fetch(updatePersonResult.loading, updatePersonResult.error, updatePersonResult.data?.updatePerson)
  }, [updatePersonResult])

  useEffect(() => {
    fetch(loading, error, data?.current)
  }, [loading, error])

  return [
    result
    , {
      createOfflinePerson(firstName: string, lastName: string, email: string) {
        createOfflinePerson({
          variables: {
            firstName: firstName,
            lastName: lastName,
            email: email
          }
        })
      }
      , updateAvatar(id: string, avatar: string) {
        updateAvatar({
          variables: {
            id: id,
            avatar: avatar
          }
        })
      },
      current() {
        current()
      },
      updatePerson(p: Person) {
        updatePerson({variables: {person: p}})
      },
      getPersonById(id: string) {
        getPersonById({
          variables: {
            id: id
          }
        })
      }
    }
  ]
}

export const PersonService: PersonApi = {
  createOfflinePerson(firstName: string, lastName: string, email: string) {
    console.error('not implemented yet')

  },
  updateAvatar(id: string, avatar: string) {
    console.log(id, avatar)

  },
  getPersonById(id: string) {
    console.log(id)

  },
  updatePerson(p: Person) {
    console.log(p)
  },

  current: () => {
    const [result, setResult] = useState();
    const {loading, error, data} = useQuery(GET_CURRENT_PERSON, {})
    const fetch = () => {
      if (!loading && data?.current) {
        setResult(data.current)
      }
      if (error) {
        console.error(error)
      }
    }

    useEffect(() => {
      fetch()
    }, [loading, error])

    return [
      result
    ]
  }
}
